import { c as _c } from "react/compiler-runtime";
import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
type Options = {
  disabled?: boolean;
  revision: string;
  onReorder: (from: number, to: number) => void;
};
const DRAG_THRESHOLD_PX = 4;
const useRailReorder = t0 => {
  const $ = _c(12);
  const {
    disabled,
    revision,
    onReorder
  } = t0;
  const listRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const dropRef = useRef(null);
  const cleanupRef = useRef(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      cleanupRef.current();
      setDragIndex(null);
      setDropIndex(null);
      dropRef.current = null;
      return () => cleanupRef.current();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== disabled || $[2] !== revision) {
    t2 = [revision, disabled];
    $[1] = disabled;
    $[2] = revision;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = y => {
      const rows = listRef.current ? Array.from(listRef.current.children) : [];
      for (let index = 0; index < rows.length; index = index + 1, index) {
        const rect = rows[index].getBoundingClientRect();
        if (y < rect.top + rect.height / 2) {
          return index;
        }
      }
      return rows.length;
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const indexFromY = t3;
  let t4;
  if ($[5] !== disabled || $[6] !== onReorder) {
    t4 = (event, index_0) => {
      if (disabled || event.button !== 0) {
        return;
      }
      cleanupRef.current();
      const startY = event.clientY;
      let active = false;
      const onMove = moveEvent => {
        if (!active) {
          if (Math.abs(moveEvent.clientY - startY) < DRAG_THRESHOLD_PX) {
            return;
          }
          active = true;
          setDragIndex(index_0);
        }
        const next = indexFromY(moveEvent.clientY);
        dropRef.current = next;
        setDropIndex(next);
      };
      const finish = commit => {
        cleanupRef.current();
        const target = dropRef.current;
        if (commit && active && target !== null) {
          const to = target > index_0 ? target - 1 : target;
          if (to !== index_0) {
            onReorder(index_0, to);
          }
        }
        setDragIndex(null);
        setDropIndex(null);
        dropRef.current = null;
      };
      const onUp = () => finish(true);
      const onCancel = () => finish(false);
      const onKeyDown = keyEvent => {
        if (keyEvent.key === "Escape") {
          finish(false);
        }
      };
      cleanupRef.current = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        window.removeEventListener("keydown", onKeyDown);
        cleanupRef.current = _temp2;
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      window.addEventListener("keydown", onKeyDown);
    };
    $[5] = disabled;
    $[6] = onReorder;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const startDrag = t4;
  let t5;
  if ($[8] !== dragIndex || $[9] !== dropIndex || $[10] !== startDrag) {
    t5 = {
      listRef,
      dragIndex,
      dropIndex,
      startDrag
    };
    $[8] = dragIndex;
    $[9] = dropIndex;
    $[10] = startDrag;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
};
export default useRailReorder;
function _temp() {}
function _temp2() {}